import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray } from '@angular/forms';
import { ClientManagerInstanceToken, ClientManagerInterface, isDefined, ModalClass } from '@evorait/pace-core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { autofillCheckpoints } from '../../store/actions/checkpoint.actions';
import { autofillFunctionalElements } from '../../store/actions/functional-elements.actions';
import {
    getAccordionGroupStateFactory,
    getCheckpointsToAutofillFromContentFactory,
    getFormAnswersByContentIdFactory,
    getFormContentByIdFactory,
    getFormHeadings,
    getFormParticipantCopyReadonly,
    getFormRadioButtonLabels,
    getFunctionalElementsToAutofillFromContentFactory,
    getIndividualizationElementStatus,
    getManualIndividualizationStatus,
    isTopicIndividualizable,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { AnswerStatus } from '../../types/answer-status';
import { Checkpoint } from '../../types/checkpoint';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentBlock } from '../../types/content-block';
import { CONTENT_TYPE, ContentChild } from '../../types/content-child';
import { FormsConstants } from '../../types/forms-constants';
import { FunctionalElement } from '../../types/functional-element';
import { Heading } from '../../types/heading';
import { RadioButtonLabels } from '../../types/radio-button-labels';
import { RADIO_OPTION_TYPE } from '../../types/radio-option';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { updateTopicVisibility } from '../../store/actions/form.actions';
import { BsModalService } from 'ngx-bootstrap/modal';
import { IndividualizationMaterialSerialComponent } from '../individualization-material-serial/individualization-material-serial.component';

@Component({
    selector: 'pace-content-renderer',
    templateUrl: './content-renderer.component.html',
    styleUrls: ['./content-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ContentRendererComponent implements OnInit, OnDestroy {
    @Input()
    public contentId: number;

    @Input()
    public parentForm: FormArray | undefined;

    public content$: Observable<ContentBlock>;
    public contentVisible$: Observable<boolean>;
    public readOnly$: Observable<boolean>;
    public autofillCheckpoints$: Observable<Checkpoint[]>;
    public autofillFunctionalElements$: Observable<FunctionalElement[]>;
    public enableIndividualization$: Observable<boolean>;
    public visibilityButton$: Observable<boolean>;

    public children$: Observable<ContentChild[]>;
    public answers$: Observable<AnswerStatus>;
    public heading$: Observable<Heading>;
    public closer$: Observable<string>;
    public headingText$: Observable<string>;
    public group$: Observable<string>;
    public groupId$: Observable<number>;
    public isTopicIndividualizable$: Observable<boolean>;

    public radioOptionsTypes = RADIO_OPTION_TYPE;
    public choicesTypes = CHOICE_TYPE;

    public radioButtonLabels$: Observable<RadioButtonLabels>;
    public contentType = CONTENT_TYPE;
    public open = false;
    private individualizationElementStatus$: Observable<boolean>;
    private destroy = new Subject<void>();
    public readonly faEye = faEye;
    public readonly faEyeSlash = faEyeSlash;
    public constructor(
        private readonly translate: TranslateService,
        private readonly store: Store<FormsState>,
        public readonly cdr: ChangeDetectorRef,
        public readonly modalService: BsModalService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public ngOnInit(): void {
        // create our own selector instances to make use of memoization
        const autoFillFunctionalElementsSelector = getFunctionalElementsToAutofillFromContentFactory({
            contentId: this.contentId,
        });

        this.content$ = this.store.select(getFormContentByIdFactory({ id: this.contentId })).pipe(filter(isDefined));

        this.enableIndividualization$ = this.store.select(getManualIndividualizationStatus).pipe(filter(isDefined));

        this.individualizationElementStatus$ = this.store
            .select(getIndividualizationElementStatus)
            .pipe(map((status) => !!status));

        this.visibilityButton$ = this.content$.pipe(
            map((content) => content.visible),
            filter(isDefined),
        );

        this.contentVisible$ = combineLatest([
            this.enableIndividualization$,
            this.content$.pipe(map((content) => !isDefined(content.visible) || content.visible)),
        ]).pipe(
            map(([isIndividualizationEnabled, isContentVisible]) => {
                return isContentVisible || isIndividualizationEnabled;
            }),
            tap(() => {
                this.cdr.markForCheck();
                //   Due to the issue where the UI is not updating immediately after data changes,
                //  we use `setTimeout` to schedule `cdr.detectChanges()` to run in the next event loop cycle.
                //  This ensures that the Angular change detection mechanism has completed its current cycle,
                // allowing manual change detection to take effect and update the UI accordingly.
                setTimeout(() => this.cdr.detectChanges());
            }),
        );

        this.readOnly$ = this.store.select(getFormParticipantCopyReadonly).pipe(
            tap(() => {
                this.cdr.markForCheck();
                this.cdr.detectChanges();
            }),
        );

        this.autofillCheckpoints$ = this.store
            .select(getCheckpointsToAutofillFromContentFactory({ contentId: this.contentId }))
            .pipe(takeUntil(this.destroy));

        this.autofillFunctionalElements$ = this.store
            .select(autoFillFunctionalElementsSelector)
            .pipe(takeUntil(this.destroy));

        this.radioButtonLabels$ = this.store.select(getFormRadioButtonLabels);
        this.answers$ = this.store.select(getFormAnswersByContentIdFactory({ id: this.contentId })).pipe(
            filter(isDefined),
            distinctUntilChanged((oldAnswers, newAnswers) => _.isEqual(oldAnswers, newAnswers)),
            tap(() => {
                this.cdr.markForCheck();
                this.cdr.detectChanges();
            }),
        );

        this.children$ = this.content$.pipe(map((content) => content.children));

        this.heading$ = combineLatest([
            this.children$.pipe(
                map((children) => children.filter((child) => child.type === CONTENT_TYPE.HEADING)[0]),
                filter(isDefined),
            ),
            this.store.select(getFormHeadings),
        ]).pipe(
            map(([heading, headings]) => headings[heading.id]),
            filter(isDefined),
        );

        this.closer$ = this.heading$.pipe(
            map(
                (heading) =>
                    `${<string>this.translate.instant('F4M_CONTENT_CLOSE_SECTION')} "${heading.chapterNumber} ${
                        heading.heading
                    }"`,
            ),
        );

        this.headingText$ = combineLatest([this.heading$, this.answers$]).pipe(
            map(([heading, answers]) => {
                const totalItems = answers.totalFunctionalElements + answers.totalCheckpoints;
                const totalAnsweredItems = answers.answeredFunctionalElements + answers.answeredCheckpoints;
                return totalItems > 0
                    ? `${heading.chapterNumber} ${heading.heading} (${totalAnsweredItems}/${totalItems})`
                    : `${heading.chapterNumber} ${heading.heading}`;
            }),
        );

        this.group$ = this.heading$.pipe(
            map((headingElement) => `${FormsConstants.SECTION_TEXT}-${headingElement.chapterNumber}`),
        );
        this.groupId$ = this.heading$.pipe(map((headingElement) => headingElement.id));

        combineLatest([this.group$, this.groupId$])
            .pipe(
                mergeMap(([group, groupId]) =>
                    this.store.select(getAccordionGroupStateFactory({ groupId: `${group}-${groupId}` })),
                ),
                takeUntil(this.destroy),
            )
            .subscribe((open) => {
                this.open = open;
                this.cdr.markForCheck();
            });

        this.isTopicIndividualizable$ = this.store.select(isTopicIndividualizable({ id: this.contentId }));
    }

    public ngOnDestroy(): void {
        this.destroy.next();
    }

    public onAstoriaAutofillClick(selected: RADIO_OPTION_TYPE): void {
        this.store.dispatch(autofillCheckpoints({ selected, id: this.contentId }));
        this.cdr.detectChanges();
    }

    public onCosimaAutofillClick(selected: CHOICE_TYPE): void {
        this.store.dispatch(autofillFunctionalElements({ selected, id: this.contentId }));
        this.cdr.detectChanges();
    }

    public trackByFn(index: number, item: ContentChild): number {
        return item.id;
    }

    public updateTopicVisibility(visibility: boolean): void {
        this.individualizationElementStatus$
            .pipe(
                take(1),
                mergeMap((status) => {
                    if (status && !this.clientManager.isPaceFormsFI() && !this.clientManager.isPaceFormsForAuthors()) {
                        const modalInstance = this.modalService.show(IndividualizationMaterialSerialComponent, {
                            backdrop: 'static',
                            class: ModalClass.MODAL_MEDIUM,
                            initialState: {
                                contentId: this.contentId,
                            },
                            ariaLabelledBy: 'individualization-material-serial-modal-title',
                        });

                        const obs$ = modalInstance.content?.modalResult.asObservable() ?? EMPTY;

                        return obs$.pipe(
                            map((results) => {
                                const action = updateTopicVisibility({
                                    visibility,
                                    id: this.contentId,
                                    materialNumber: results.materialNumber,
                                    serialNumber: results.serialNumber,
                                    description: results.description,
                                });
                                return action;
                            }),
                        );
                    } else {
                        const action = updateTopicVisibility({
                            visibility,
                            id: this.contentId,
                        });
                        return of(action);
                    }
                }),
            )
            .subscribe((action) => {
                this.store.dispatch(action);
            });
    }
}
